import {
    AccountType, IAssetAllocationTreeNode,
    IBenchmarkReturn, IFeesTreeNode,
    IPerformanceTreeNode,
    IPlanTreeNode,
} from "../../model/Portfolio.model";
import {IPortfolioFeesTreeNode, IUniverseReturn} from "../../api/PortfolioApi";
import {getTotalNode} from "./portfolioUtil";

export const buildAssetAllocationTreeByDate = (datedFlatNodes: IPlanTreeNode[], date: string): IAssetAllocationTreeNode[] => {
    const flatNodes = datedFlatNodes.filter(node => node.date === date);
    if (flatNodes.length === 0) {
        return [];
    }
    const totalPlanNode = getTotalNode(flatNodes);
    return totalPlanNode ? getAssetAllocationChildNodes(flatNodes, totalPlanNode.id) : [];
};

export const buildPerformanceTreeByDate =
    (datedFlatNodes: IPlanTreeNode[],
     date: string,
     datedBenchmarkReturns: IBenchmarkReturn[],
     universeReturns: IUniverseReturn[],
     isDcPlan: boolean): IPerformanceTreeNode[] => {
        const flatNodes = datedFlatNodes.filter(node => node.date === date);

        if (flatNodes.length === 0) {
            return [];
        }
        const benchmarks = datedBenchmarkReturns.filter(benchmark => benchmark.date === date);
        const datedUniverseReturns = universeReturns?.filter(pr => pr.date === date);
        const totalPlanNode = getTotalNode(flatNodes);
        return totalPlanNode
            ? getPerformanceChildNodes(flatNodes, totalPlanNode.id, benchmarks, datedUniverseReturns, isDcPlan)
            : [];
    };

export const buildFeeTree = (flatNodes: IPortfolioFeesTreeNode[]): IFeesTreeNode[] => {
    const totalFeeNode = getTotalNode(flatNodes);
    return totalFeeNode ? getFeeChildNodes(flatNodes, totalFeeNode.id) : [];
};

const getUniverseReturnNode = (
    parentNode: IPlanTreeNode,
    universeReturns: IUniverseReturn[]
): IPerformanceTreeNode | null => {
    const universeReturn = universeReturns.find(it => it.accountId === parentNode.id);
    if (!universeReturn) {
        return null;
    }
    return {
        id: parentNode.id + 100 * universeReturn.accountId,
        name: `${universeReturn.peerUniverseReturnName} Median`,
        sortOrder: 1,
        accountType: AccountType.BENCHMARK_OR_PEER,
        date: universeReturn.date,
        nodes: null,
        relativePerformance: universeReturn.performance,
        absolutePerformance: universeReturn.performance
    };
};

const getBenchmarkNode = (
    parentNode: IPlanTreeNode, benchmarkReturns: IBenchmarkReturn[], universeReturns: IUniverseReturn[])
    : IPerformanceTreeNode | null => {
    const benchmark = benchmarkReturns.find(it => it.benchmarkId === parentNode.benchmarkId);
    if (!benchmark) {
        return null;
    }

    return {
        id: parentNode.id + 100 * benchmark.benchmarkId,
        name: benchmark.name,
        sortOrder: 1,
        accountType: AccountType.BENCHMARK_OR_PEER,
        date: benchmark.date,
        nodes: null,
        relativePerformance: benchmark.performance,
        absolutePerformance: benchmark.performance,
        ranking: universeReturns.length > 0 ? getRanking(parentNode.id, universeReturns, true) : null,
    };
};


const getBenchmarkAndUniverseNodes = (
    parentNode: IPlanTreeNode,
    benchmarkReturns: IBenchmarkReturn[],
    universeReturns: IUniverseReturn[]
): IPerformanceTreeNode[] | null => {
    const benchmarkNode = getBenchmarkNode(parentNode, benchmarkReturns, universeReturns);
    const universeNode = getUniverseReturnNode(parentNode, universeReturns);

    if (benchmarkNode && universeNode) {
        return [benchmarkNode, universeNode];
    }

    return benchmarkNode ? [benchmarkNode] : universeNode ? [universeNode] : null;
};

const getRanking = (accountId: number, universeReturns: IUniverseReturn[], isBenchMark: boolean = false) => {
    const universeReturn = universeReturns.find(it => it.accountId === accountId);
    if (!universeReturn) {
        return null;
    }

    return isBenchMark ? universeReturn.benchmarkRanking : universeReturn.fundRanking;
};

const getChildNodes = (flatNodes: IPlanTreeNode[],
                       benchmarkReturns: IBenchmarkReturn[],
                       universeReturns: IUniverseReturn[],
                       node: IPlanTreeNode,
                       isDcPlan: boolean):  IPerformanceTreeNode[] | null => {
    const hasBenchmarkOrUniverse = benchmarkReturns.length > 0 || universeReturns.length > 0;

    if (node.accountType === AccountType.ATOMIC) {
        return hasBenchmarkOrUniverse ? getBenchmarkAndUniverseNodes(node, benchmarkReturns, universeReturns) : null;
    }

    const performanceChildNodes =
        getPerformanceChildNodes(flatNodes, node.id, benchmarkReturns, universeReturns, isDcPlan);
    if (universeReturns.length > 0) {
        performanceChildNodes.map(it => it.ranking = getRanking(it.id, universeReturns));
    }
    if (!hasBenchmarkOrUniverse || isDcPlan) {
        return performanceChildNodes;
    }

    // For Non-DC plan add benchmark at composite level
    const benchmarkAndUniverseNodes = getBenchmarkAndUniverseNodes(node, benchmarkReturns, universeReturns);
    return benchmarkAndUniverseNodes ? benchmarkAndUniverseNodes.concat(performanceChildNodes) : performanceChildNodes;
};

const getPerformanceChildNodes =
    (flatNodes: IPlanTreeNode[],
     parentId: number,
     benchmarkReturns: IBenchmarkReturn[],
     universeReturns: IUniverseReturn[],
     isDcPlan: boolean): IPerformanceTreeNode[] => {
        return flatNodes
            .filter(it => it.parentId === parentId)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(node => ({
                id: node.id,
                name: node.name,
                sortOrder: node.sortOrder,
                accountType: node.accountType,
                date: node.date,
                backstopId: node.backstopId,
                nodes: getChildNodes(flatNodes, benchmarkReturns, universeReturns, node, isDcPlan),
                relativePerformance: node.relativePerformance,
                absolutePerformance: node.absolutePerformance,
                managementType: node.managementType,
            }));
    };

const getFeeChildNodes = (flatNodes: IPortfolioFeesTreeNode[], parentId: number): IFeesTreeNode[] => {
    return flatNodes
        .filter(it => it.parentId === parentId)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(node => ({
            id: node.id,
            name: node.name,
            sortOrder: node.sortOrder,
            accountType: node.accountType,
            date: node.date,
            backstopId: node.backstopId,
            peerUniverseName: node.peerUniverseName,
            netExpenseRatio: node.netExpenseRatio,
            revenueShare: node.revenueShare,
            effectiveNetExpenseRatio: node.effectiveNetExpenseRatio,
            peerUniverseMedian: node.peerUniverseMedian,
            percentile25: node.percentile25,
            percentile50: node.percentile50,
            percentile75: node.percentile75,
            population: node.population,
            nodes: node.accountType === AccountType.ATOMIC ? null : getFeeChildNodes(flatNodes, node.id),
        } as IFeesTreeNode));
};

const getAssetAllocationChildNodes = (flatNodes: IPlanTreeNode[], parentId: number): IAssetAllocationTreeNode[] => {
    return flatNodes
        .filter(it => it.parentId === parentId)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(node => ({
            id: node.id,
            name: node.name,
            sortOrder: node.sortOrder,
            accountType: node.accountType,
            marketValue: node.marketValue,
            marketValuePctOfTotal: node.marketValuePctOfTotal,
            marketValuePctOfParent: node.marketValuePctOfParent,
            backstopId: node.backstopId,
            nodes: node.accountType === AccountType.ATOMIC ? null : getAssetAllocationChildNodes(flatNodes, node.id),
            rating: node.rating ? node.rating : "",
            priorMarketValuePctOfTotal: node.priorMarketValuePctOfTotal,
            date: node.date,
        }));
};